import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../context/NotificationContext';

export const NotificationBell: React.FC = () => {
    const { items, noLeidas, fetchNotificaciones, marcarLeida, marcarTodasLeidas, delete: deleteNotif } = useNotifications();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const formatFecha = (iso: string): string => {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        const min = Math.floor(diff / 60000);
        if (min < 1) return 'ahora';
        if (min < 60) return `${min}m`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h}h`;
        return `${Math.floor(h / 24)}d`;
    };

    const iconByTipo = (tipo: string): string => {
        if (tipo.includes('wip')) return '⚠️';
        if (tipo.includes('bloque')) return '🚫';
        if (tipo.includes('asign')) return '👤';
        if (tipo.includes('backlog')) return '📋';
        return '🔔';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => {
                    setOpen(!open);
                    if (!open) fetchNotificaciones();
                }}
                className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Notificaciones"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {noLeidas > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {noLeidas > 9 ? '9+' : noLeidas}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 max-h-[500px] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between p-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800">Notificaciones</h3>
                        {noLeidas > 0 && (
                            <button
                                onClick={marcarTodasLeidas}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                                Marcar todas leídas
                            </button>
                        )}
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {items.length === 0 ? (
                            <div className="py-8 text-center text-slate-400 text-sm">
                                <div className="text-3xl mb-2">🎉</div>
                                Sin notificaciones
                            </div>
                        ) : (
                            items.map(n => (
                                <div
                                    key={n.id}
                                    className={`p-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                                        !n.leida ? 'bg-blue-50/50' : ''
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="text-lg">{iconByTipo(n.tipo)}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className={`text-sm ${!n.leida ? 'font-semibold' : 'font-medium'} text-slate-800`}>
                                                    {n.titulo}
                                                </h4>
                                                {!n.leida && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                                            </div>
                                            <p className="text-xs text-slate-600 mt-0.5">{n.mensaje}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-[10px] text-slate-400">{formatFecha(n.creado_en)}</span>
                                                <div className="flex gap-2">
                                                    {!n.leida && (
                                                        <button
                                                            onClick={() => marcarLeida(n.id)}
                                                            className="text-[10px] text-blue-600 hover:text-blue-800"
                                                        >
                                                            Marcar leída
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteNotif(n.id)}
                                                        className="text-[10px] text-slate-400 hover:text-red-600"
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};